/* eslint-disable complexity -- source-parity download flow */
import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';
import type { FileMetadata, HeliaNode, ProgressCallback } from '@/types/poc/helia';
import type { RecipientEncryptionResult } from '@/types/poc/auth';
import type { FileStoreService } from '@/services/abstract/poc/FileStoreService';
import { FileEncryptionService } from '@/services/abstract/poc/fileEncryption.service.interface';
import { ensureFileCloned } from '@/store/thunks/poc/cloneQueue';
import { ensureDecapsPrivateKey } from '@/store/thunks/auth/ensureDecapsPrivateKey';

// Timeout for fetching the encryption key blob from Helia
const ENCRYPTION_FETCH_TIMEOUT_MS = 30_000;

// Progress constants
const PROGRESS_KEY_FETCH = 10;
const PROGRESS_ENCRYPTION_FETCH = 15;
const PROGRESS_KEY_READY = 25;
const PROGRESS_CLONE_STARTED = 35;
const PROGRESS_FILE_READ = 60;
const PROGRESS_FILE_DECRYPT = 75;
const PROGRESS_COMPLETE = 100;

/**
 * Fetches a recipient's encrypted file key blob from Helia by CID and parses it
 * into a RecipientEncryptionResult. The blob is addressed via decryptionCIDS
 * (userId -> CID), which the backend read-model populates for every file.
 */
async function fetchRecipientKeyBlob(
  fileStoreService: FileStoreService,
  helia: HeliaNode,
  cid: string,
  recipientId: string
): Promise<RecipientEncryptionResult> {
  try {
    const encryptionJsonBytes = await fileStoreService.getFile(helia, cid, {
      signal: AbortSignal.timeout(ENCRYPTION_FETCH_TIMEOUT_MS),
    });
    return FileEncryptionService.parseRecipientKeyBlob(
      new TextDecoder().decode(new Uint8Array(encryptionJsonBytes)),
      recipientId
    );
  } catch (fetchError) {
    throw new Error(
      `Failed to fetch encryption data: ${fetchError instanceof Error ? fetchError.message : 'Unknown error'}`
    );
  }
}

/**
 * Download and decrypt an encrypted file as a recipient.
 *
 * Flow:
 * 1. Validate user auth and resolve encryption data
 * 2. Ensure file is cloned locally (via ensureFileCloned — handles preemptive cloning)
 * 3. Read encrypted file from local Helia blockstore (fast, no network)
 * 4. Decrypt and return
 */
export const downloadAndDecryptFile = createAppAsyncThunk(
  'helia/downloadAndDecrypt',
  async(
    {
      sixId,
      fileMetadata,
      onProgress
    }: {
      sixId: string;
      fileMetadata: FileMetadata;
      onProgress?: ProgressCallback;
    },
    { extra: { pocServices, userService, sixDetailService, loggerService }, dispatch, getState, rejectWithValue }
  ) => {
    const log = loggerService.createLogger({ namespace: 'six3ro:p2p:files:download' });

    try {
      log.info('Starting download for file:', fileMetadata.name, '(CID:', fileMetadata.cid, ')');

      // Step 1: Validate user
      log.debug('Step 1: Validating user and permissions...');
      const state = getState();

      if (state.user.user?.id == null) {
        log.error('User not authenticated');
        throw new Error('User not authenticated');
      }

      const currentUserId = state.auth.userId ?? state.user.user.id;

      // Step 2: Resolve the recipient's encrypted file key via decryptionCIDS
      // (userId -> key-blob CID, populated by the backend read-model). The blob
      // lives in Helia and is fetched by CID, then parsed.
      log.debug('Step 2: Resolving encryption data...');

      if (fileMetadata.decryptionCIDS == null || fileMetadata.decryptionCIDS.length === 0) {
        throw new Error('No encryption data found for this file');
      }

      const ourDecryptionCID = fileMetadata.decryptionCIDS.find(
        entry => entry.userId === currentUserId
      );
      if (ourDecryptionCID == null) {
        log.error('Current user is not a recipient');
        throw new Error('Current user is not a recipient of this file');
      }

      log.debug('Fetching encryption data from CID:', ourDecryptionCID.cid);
      if (onProgress != null) onProgress(PROGRESS_ENCRYPTION_FETCH, PROGRESS_COMPLETE);

      const keyBlobHelia = pocServices.p2pStackManagerService.getHeliaNode();
      if (keyBlobHelia === null) {
        throw new Error('Helia node not initialized - cannot fetch encryption data');
      }

      const recipientResult = await fetchRecipientKeyBlob(
        pocServices.fileStoreService,
        keyBlobHelia,
        ourDecryptionCID.cid,
        currentUserId
      );
      log.debug('Resolved encryption data for user:', recipientResult.recipientId);

      // Step 3: Ensure we have the decrypted private key
      log.debug('Step 3: Getting decryption key...');
      if (state.auth.decapsKey.length === 0 && onProgress != null) {
        onProgress(PROGRESS_KEY_FETCH, PROGRESS_COMPLETE);
      }

      const recipientPrivateKey = await ensureDecapsPrivateKey({
        decapsKeyHex: state.auth.decapsKey,
        ukekHex: state.auth.ukek,
        userId: currentUserId,
        dispatch,
        userService,
        cryptoService: pocServices.cryptoService,
        log,
      });

      if (onProgress != null) onProgress(PROGRESS_KEY_READY, PROGRESS_COMPLETE);

      // Step 4: Ensure file is cloned locally (handles preemptive cloning)
      log.debug('Step 4: Ensuring file is cloned locally...');
      if (onProgress != null) onProgress(PROGRESS_CLONE_STARTED, PROGRESS_COMPLETE);

      await dispatch(ensureFileCloned({ sixId, fileMetadata })).unwrap();
      log.debug('File confirmed local');

      // Step 5: Read encrypted file from local Helia (guaranteed fast — no network)
      log.debug('Step 5: Reading encrypted file from local blockstore...');
      if (onProgress != null) onProgress(PROGRESS_FILE_READ, PROGRESS_COMPLETE);

      const helia = pocServices.p2pStackManagerService.getHeliaNode();
      if (helia === null) {
        throw new Error('Helia node not initialized');
      }

      const encryptedFileData = await pocServices.fileStoreService.getFile(helia, fileMetadata.cid);
      log.debug('Read', encryptedFileData.length, 'bytes from local blockstore');

      const encryptedFile = new File([new Uint8Array(encryptedFileData)], fileMetadata.name, {
        type: 'application/octet-stream'
      });

      // Step 6: Decrypt the file
      log.debug('Step 6: Decrypting file...');
      if (onProgress != null) onProgress(PROGRESS_FILE_DECRYPT, PROGRESS_COMPLETE);
      pocServices.cidDownloadLogger.log(fileMetadata.cid, 'decrypting', { fileName: fileMetadata.name });

      const decryptionResult = await pocServices.fileEncryptionService.decryptFileAsRecipient(
        encryptedFile,
        recipientResult,
        recipientPrivateKey
      );
      log.debug('File decrypted successfully');

      pocServices.cidDownloadLogger.log(fileMetadata.cid, 'complete', {
        originalSize: decryptionResult.originalFileSize,
        fileName: decryptionResult.originalFileName
      });
      if (onProgress != null) onProgress(PROGRESS_COMPLETE, PROGRESS_COMPLETE);

      // Notify server that file was downloaded (fire-and-forget)
      sixDetailService.markFileDownloaded(sixId, fileMetadata.id)
        .then(() => { log.debug('Marked file as downloaded on server'); })
        .catch((error: unknown) => { log.warn('Failed to mark file as downloaded:', error); });

      log.info('Download and decryption complete!');
      return {
        decryptedFile: decryptionResult.decryptedFile,
        originalFileName: decryptionResult.originalFileName,
        originalFileSize: decryptionResult.originalFileSize,
        originalFileType: decryptionResult.originalFileType
      };

    } catch (error) {
      log.error('Fatal error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error decrypting file';
      return rejectWithValue({ message: errorMessage });
    }
  }
);
